import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import { ToastProvider } from "@swordsweeper/ssi-ui-component-library";
import ClinicDesktopPage from "@src/components/pages/clinic-portal/ClinicDesktopPage/";
import ProductListPage from "@src/components/pages/clinic-portal/ProductListPage";
import ProductRecommendationsPage from "@src/components/pages/clinic-portal/ProductRecommendationsPage";
import ProductDetailPage from "@src/components/pages/clinic-portal/ProductDetailPage";
import CartPage from "@src/components/pages/clinic-portal/CartPage";
import CheckoutPage from "@src/components/pages/clinic-portal/CheckoutPage";
import OrdersPage from "@src/components/pages/clinic-portal/OrdersPage";
import OrderPlacedPage from "@src/components/pages/clinic-portal/OrderPlacedPage";
import AccountPage from "@src/components/pages/clinic-portal/AccountPage";
// Note: Commented out for Beta - messaging function is hidden, beta will not have messaging
// import MessagesPage from "@src/components/pages/clinic-portal/MessagesPage";
import SupportPage from "@src/components/pages/clinic-portal/SupportPage";
import CovetrusAuthCallbackPage from "@src/components/pages/clinic-portal/CovetrusAuthCallbackPage";
import SearchResultsPage from "@src/components/pages/clinic-portal/SearchResultsPage/SearchResultsPage";
import TermsUpdatePage from "@src/components/pages/clinic-portal/TermsUpdatePage";
import SignUpPage from "@src/components/pages/clinic-portal/SignUpPage";
import EmailSentPage from "@src/components/pages/clinic-portal/EmailSentPage";
import ClinicSetupPage from "@src/components/pages/clinic-portal/ClinicSetupPage";
import ClinicPendingPage from "@src/components/pages/clinic-portal/ClinicPendingPage";
import FavoritesPage from "@src/components/pages/clinic-portal/FavoritesPage/FavoritesPage";
import PromotedProductsPage from "@src/components/pages/clinic-portal/PromotedProductsPage";
import ManageClinicPage from "@src/components/pages/clinic-portal/ManageClinicPage";
import ManageClinicDetailPage from "@src/components/pages/clinic-portal/ManageClinicDetailPage";
import ManageTeamPage from "@src/components/pages/clinic-portal/ManageTeamPage";
import ErrorPage from "@src/components/pages/clinic-portal/ErrorPage";
import NotFoundPage from "@src/components/pages/clinic-portal/NotFoundPage";
import SimilarProductsPage from "@src/components/pages/clinic-portal/SimilarProductsPage";
import AdminComingSoonPage from "@src/components/pages/admin-portal/AdminComingSoonPage";
import SupplierComingSoonPage from "@src/components/pages/supplier-portal/SupplierComingSoonPage";
import SupportComingSoonPage from "@src/components/pages/support-portal/SupportComingSoonPage";
import AppContainer from "@src/components/containers/AppContainer";
import RequiresPermissionContainer from "@src/components/containers/RequiresPermissionContainer";
import RequiresClinicAdminContainer from "@src/components/containers/RequiresClinicAdminContainer";
import PortalGuardRoute from "@src/components/containers/PortalGuardRoute";
import OfflineBanner from "@src/components/widgets/OfflineBanner";
import NetworkStatusProvider from "@src/providers/NetworkStatusProvider";
import Permissions from "@src/constants/Permissions";
import PermissionLevels from "@src/constants/PermissionLevels";
import Roles from "@src/constants/Roles";
import { loadPendoScript } from "@src/utils/pendo";
import RequiresTermsContainer from "@src/components/containers/RequiresTermsContainer/RequiresTermsContainer";
import RequiresActiveClinicContainer from "@src/components/containers/RequiresActiveClinicContainer/RequiresActiveClinicContainer";
import { useCurrentUserStore } from "@src/state/currentUserState";

const isQa = process.env.NODE_ENV === "qa";
const isProd = process.env.NODE_ENV === "production";

// Load Pendo script and sentry on app initialization (qa or production only)

if (isQa || isProd) {
  loadPendoScript();
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    integrations: [browserTracingIntegration()],
    environment: process.env.ENVIRONMENT,
    tracesSampleRate: 1.0,
    debug: false,
    attachStacktrace: true,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "No matching state found in storage",
      "Unauthorized",
      "TypeError: Cannot read properties of null (reading 'zoom')",
    ],
  });
}

// Dispatches the home route "/" to the appropriate portal home based on the user's role.
// Clinic Portal users go through the active-clinic and terms checks before reaching the dashboard.
function PortalHomeDispatcher(): React.JSX.Element | null {
  const { currentUser, loading, distributorConnections } =
    useCurrentUserStore();

  if (
    loading ||
    !currentUser ||
    (currentUser?.role === Roles.ClinicUser &&
      !Array.isArray(distributorConnections))
  )
    return null;

  switch (currentUser.role) {
    case Roles.ClinicUser: {
      if (currentUser.clinicUsers.length === 0) {
        return <Navigate to="/clinic-setup" replace />;
      }
      const defaultClinicUser =
        currentUser.clinicUsers.find((cu) => cu.isDefault) ??
        currentUser.clinicUsers[0];
      if (defaultClinicUser.status !== "active") {
        return <Navigate to="/clinic-pending" replace />;
      }
      const needsTermsUpdate = distributorConnections?.some(
        (c) => c.needsTermsUpdate === true,
      );
      if (needsTermsUpdate) {
        return <Navigate to="/terms-update" replace />;
      }
      return <ClinicDesktopPage />;
    }
    case Roles.SiteAdmin:
      return <AdminComingSoonPage />;
    case Roles.DistributorAdmin:
      return <SupplierComingSoonPage />;
    case Roles.SupportAgent:
      return <SupportComingSoonPage />;
    default:
      return <NotFoundPage />;
  }
}

const routesJsx = (
  <BrowserRouter
    basename="/app"
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <Routes>
      {/* Pre-auth / onboarding routes — outside AppContainer */}
      <Route path="/terms-update" element={<TermsUpdatePage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/sign-up/email-sent" element={<EmailSentPage />} />
      <Route path="/clinic-setup" element={<ClinicSetupPage />} />
      <Route path="/clinic-pending" element={<ClinicPendingPage />} />

      <Route element={<AppContainer />}>
        {/* Home — role-based dispatcher, handles all portal home views */}
        <Route path="" element={<PortalHomeDispatcher />} />

        {/* Clinic Portal — all routes protected to clinic_user role */}
        <Route element={<PortalGuardRoute role={Roles.ClinicUser} />}>
          <Route element={<RequiresActiveClinicContainer />}>
            <Route element={<RequiresTermsContainer />}>
              <Route
                path="/product-category/:productCategoryId"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.ProductCategories,
                      level: PermissionLevels.View,
                    }}
                    element={<ProductListPage />}
                  />
                }
              />
              <Route
                path="/product/:productId/similar-products"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<SimilarProductsPage />}
                  />
                }
              />
              <Route
                path="/product/:productId"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<ProductDetailPage />}
                  />
                }
              />
              <Route
                path="/product-recommendations/:type?"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<ProductRecommendationsPage />}
                  />
                }
              />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route
                path="/orders"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Orders,
                      level: PermissionLevels.View,
                    }}
                    element={<OrdersPage />}
                  />
                }
              />
              <Route
                path="/orders/:orderNumber"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Orders,
                      level: PermissionLevels.View,
                    }}
                    element={<OrderPlacedPage />}
                  />
                }
              />
              <Route path="/account/:view?" element={<AccountPage />} />
              <Route
                path="/manage-clinic"
                element={
                  <RequiresClinicAdminContainer
                    element={<ManageClinicPage />}
                  />
                }
              />
              <Route
                path="/manage-clinic/:clinicId"
                element={
                  <RequiresClinicAdminContainer
                    element={<ManageClinicDetailPage />}
                  />
                }
              />
              <Route
                path="/manage-team"
                element={
                  <RequiresClinicAdminContainer element={<ManageTeamPage />} />
                }
              />
              {/* Note: Commented out for Beta - messaging function is hidden, beta will not have messaging */}
              {/* <Route
                path="/messages/:messageThreadId?"
                element={<MessagesPage />}
              /> */}
              <Route path="/support" element={<SupportPage />} />
              <Route
                path="/favorites"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<FavoritesPage />}
                  />
                }
              />
              <Route
                path="/promoted-products/:distributorSlug"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<PromotedProductsPage />}
                  />
                }
              />
              <Route
                path="/callback/covetrus"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Distributors,
                      level: PermissionLevels.View,
                    }}
                    element={<CovetrusAuthCallbackPage />}
                  />
                }
              />
              <Route
                path="/search"
                element={
                  <RequiresPermissionContainer
                    permission={{
                      slug: Permissions.Products,
                      level: PermissionLevels.View,
                    }}
                    element={<SearchResultsPage />}
                  />
                }
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  </BrowserRouter>
);

const contextJsx = (
  <NetworkStatusProvider>
    <OfflineBanner />
    <ToastProvider>{routesJsx}</ToastProvider>
  </NetworkStatusProvider>
);

const RootApp = isProd ? (
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      {contextJsx}
    </Sentry.ErrorBoundary>
  </React.StrictMode>
) : (
  contextJsx
);

const container = document.getElementById("app");
if (container) {
  createRoot(container).render(RootApp);
}
